import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { copyrightLine } from "@/config/app";
import { installerBranding } from "@/branding/Branding";

/**
 * Reusable renderer for a legal document (Privacy Policy, Terms of Use).
 * `sections` is a mixed array of headings, paragraphs, and bullet lists.
 * Ports the mobile <LegalDocument /> component but styled for the web portal.
 */
export function LegalDocument({ title, subtitle, effectiveDate, sections, testid }) {
    const navigate = useNavigate();
    return (
        <div className="min-h-full bg-[#F5F5F7] pb-16" data-testid={testid}>
            <div className="mx-auto max-w-3xl px-4 md:px-8 pt-6 md:pt-10">
                <button
                    onClick={() => navigate(-1)}
                    data-testid="legal-back"
                    className="flex items-center gap-2 text-sm text-[#4B5565] hover:text-[#131B2F] transition-colors mb-4"
                >
                    <ArrowLeft size={16} strokeWidth={1.75} /> Back
                </button>

                <article className="bg-white rounded-[20px] border border-[#E5E7EB] p-6 md:p-10 shadow-[0_4px_12px_rgba(11,18,32,0.04)]">
                    <span className="inline-block px-3 py-1 rounded-full bg-[#3E9E2E]/12 text-[#3E9E2E] text-xs font-medium tracking-[0.05em]">
                        Legal
                    </span>
                    <h1 className="mt-4 text-2xl md:text-3xl text-[#131B2F] font-medium">{title}</h1>
                    {subtitle && <p className="mt-2 text-sm md:text-base text-[#4B5565] leading-relaxed">{subtitle}</p>}
                    <div className="mt-4 mb-2 w-9 h-0.5 bg-[#3E9E2E] rounded-full" />
                    <p className="text-sm text-[#4B5565] italic">Effective {effectiveDate}</p>

                    <div className="mt-6 space-y-4">
                        {sections.map((s, idx) => {
                            if (s.type === "h") return <h2 key={idx} className="text-base md:text-lg text-[#131B2F] font-medium pt-2">{s.text}</h2>;
                            if (s.type === "p") return <p key={idx} className="text-sm md:text-base text-[#131B2F] leading-[1.65]">{s.text}</p>;
                            return (
                                <ul key={idx} className="space-y-2 pl-1">
                                    {s.items.map((it, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#3E9E2E] shrink-0" />
                                            <span className="text-sm md:text-base text-[#131B2F] leading-[1.65]">{it}</span>
                                        </li>
                                    ))}
                                </ul>
                            );
                        })}
                    </div>

                    <div className="mt-8 p-5 rounded-[16px] bg-[#3E9E2E]/6 border border-[#3E9E2E]/20">
                        <div className="text-base text-[#131B2F] font-medium">Contact us</div>
                        <p className="mt-1 text-sm text-[#4B5565]">Questions or requests about this document? Get in touch:</p>
                        <a
                            href={`mailto:${installerBranding.email}`}
                            data-testid="legal-contact-email"
                            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3E9E2E]/25 text-sm text-[#3E9E2E] font-medium hover:bg-[#3E9E2E]/8 transition-colors"
                        >
                            <Mail size={14} strokeWidth={1.75} />
                            {installerBranding.email}
                        </a>
                    </div>
                </article>

                <p className="mt-6 text-center text-xs text-[#4B5565]">{copyrightLine()}</p>
            </div>
        </div>
    );
}

export function LegalPlaceholder({ title, intro, testid }) {
    const navigate = useNavigate();
    return (
        <div className="min-h-full bg-[#F5F5F7] pb-16" data-testid={testid}>
            <div className="mx-auto max-w-3xl px-4 md:px-8 pt-6 md:pt-10">
                <button
                    onClick={() => navigate(-1)}
                    data-testid="legal-back"
                    className="flex items-center gap-2 text-sm text-[#4B5565] hover:text-[#131B2F] transition-colors mb-4"
                >
                    <ArrowLeft size={16} strokeWidth={1.75} /> Back
                </button>
                <article className="bg-white rounded-[20px] border border-[#E5E7EB] p-6 md:p-10">
                    <span className="inline-block px-3 py-1 rounded-full bg-[#3E9E2E]/12 text-[#3E9E2E] text-xs font-medium">Coming soon</span>
                    <h1 className="mt-4 text-2xl md:text-3xl text-[#131B2F] font-medium">{title}</h1>
                    <div className="mt-4 mb-4 w-9 h-0.5 bg-[#3E9E2E] rounded-full" />
                    <p className="text-base md:text-lg text-[#131B2F] leading-[1.6]">{intro}</p>
                    <p className="mt-4 text-sm text-[#4B5565] leading-[1.6]">
                        The final text for this section is being prepared and will be published in a future update of Pearl.
                    </p>
                </article>
                <p className="mt-6 text-center text-xs text-[#4B5565]">{copyrightLine()}</p>
            </div>
        </div>
    );
}
